import {
  BorrowExceedsSafeLtvError,
  ExcessiveSlippageToleranceError,
  InsufficientSharedLiquidityError,
  MissingMarketPriceError,
  RepayExceedsDebtError,
  RepaySharesExceedDebtError,
  WithdrawExceedsCollateralError,
  WithdrawExceedsSupplyError,
  WithdrawMakesPositionUnhealthyError,
  WithdrawSharesExceedSupplyError
} from '@morpho-org/morpho-sdk'

/**
 * Maps a market-actions-v2 failure to a short, user-friendly finalize-button message,
 * the market sister of the Vault V2 inline "Insufficient X balance" copy. One place so
 * the two simple-action panels and the two Position Manager dialogs surface the same
 * wording instead of leaking raw morpho-sdk error messages (e.g.
 * `BorrowExceedsSafeLtvError: ...`) to the user.
 *
 * Precedence (most specific first): an exceeded wallet balance, then a borrow-capacity
 * breach, then a repay above the outstanding loan, then a mapped build error, then a
 * generic fallback. Returns `undefined` when there is nothing to surface.
 */

/** Friendly copy for app-local errors that set `.name` to their class. */
const MESSAGE_BY_ERROR_NAME: Record<string, string> = {
  BlueBundlesV1NotDeployedError: 'Market actions are not available on this network yet'
}

/**
 * The morpho-sdk build errors do NOT set `.name` (it stays 'Error'), so the name map above
 * never matches them — discriminate on `instanceof` instead.
 */
const MESSAGE_BY_ERROR_CLASS: ReadonlyArray<[new (...args: never[]) => Error, string]> = [
  [BorrowExceedsSafeLtvError, 'Borrow capacity reached'],
  [InsufficientSharedLiquidityError, 'Not enough liquidity available'],
  [RepayExceedsDebtError, 'Amount exceeds your loan'],
  [RepaySharesExceedDebtError, 'Amount exceeds your loan'],
  [MissingMarketPriceError, 'Price unavailable, please retry'],
  [ExcessiveSlippageToleranceError, 'Price moved too much, please retry'],
  [WithdrawMakesPositionUnhealthyError, 'Borrow capacity reached'],
  [WithdrawExceedsCollateralError, 'Amount exceeds your collateral'],
  [WithdrawExceedsSupplyError, 'Amount exceeds your position'],
  [WithdrawSharesExceedSupplyError, 'Amount exceeds your position']
]

/** Shown for any build error we don't have specific copy for. */
const GENERIC_ERROR_MESSAGE = "This transaction can't be completed"

export interface MarketActionErrorParams {
  /** Build-time error thrown by the morpho-sdk action builder (e.g. `BorrowExceedsSafeLtvError`). */
  buildError?: unknown
  /** Token symbol whose entered amount exceeds the wallet/sourceable balance → balance error. */
  insufficientBalanceSymbol?: string
  /** A borrow / collateral-withdraw that would push the position past the safe LTV. */
  exceedsBorrowCapacity?: boolean
  /** A repay above the position's outstanding loan. */
  exceedsLoan?: boolean
}

/** Copy for the review-dialog alert slot; null when neither error is set (alert hidden). */
export const getMarketActionAlertMessage = ({
  buildError,
  marketError
}: {
  buildError: unknown
  marketError: unknown
}): string | null => {
  if (buildError) {
    return getMarketActionError({ buildError }) ?? 'Unable to prepare this transaction'
  }
  if (marketError) return 'Unable to refresh market data, please retry'
  return null
}

export const getMarketActionError = ({
  buildError,
  insufficientBalanceSymbol,
  exceedsBorrowCapacity,
  exceedsLoan
}: MarketActionErrorParams): string | undefined => {
  if (insufficientBalanceSymbol) return `Insufficient ${insufficientBalanceSymbol} balance`
  if (exceedsBorrowCapacity) return 'Borrow capacity reached'
  if (exceedsLoan) return 'Amount exceeds your loan'
  if (buildError instanceof Error) {
    for (const [ErrorClass, message] of MESSAGE_BY_ERROR_CLASS) {
      if (buildError instanceof ErrorClass) return message
    }
    return MESSAGE_BY_ERROR_NAME[buildError.name] ?? GENERIC_ERROR_MESSAGE
  }
  return undefined
}
